// Role → permission map for sidebar visibility & button enablement.
// Single source of truth so it matches backend `require_roles`.

export const VIEWER_ROLES = ["trustee_viewer", "auditor_viewer", "rta_viewer"];

// Single source of truth for role-scoped route whitelists. Referenced by
// ProtectedRoute.jsx and Shell.jsx so the two guards can't drift apart.
export const INVESTOR_ALLOWED_ROUTES = [
  "/investor",
  "/investor/documents",
  "/investor/statements",
  "/investor/redeem",
  "/investor/kyc",
  "/security",
  "/help",
];

export const DISTRIBUTOR_ALLOWED_ROUTES = [
  "/distributor",
  "/distributor/investor",
  "/profile",
  "/billing",
  "/security",
  "/security/change-password",
  "/help",
];

// Map each route key to the set of roles allowed to *see* it in the sidebar.
// super_admin sees everything; investor uses INVESTOR_NAV.
const ALL = ["super_admin", "fund_admin", "accountant", "compliance_officer",
  "fund_manager", "relationship_manager", "trustee_viewer", "auditor_viewer", "rta_viewer"];
const ALL_PLUS_INVESTOR = [...ALL, "investor"];

export const ROUTE_ROLES = {
  "/dashboard":        ALL,
  "/clients":          ["super_admin", "fund_admin", "fund_manager", "compliance_officer", "auditor_viewer", "trustee_viewer", "rta_viewer"],
  "/funds":            ALL,
  "/schemes":          ALL,
  "/investors":        ["super_admin", "fund_admin", "fund_manager", "relationship_manager", "compliance_officer", "accountant", "rta_viewer", "trustee_viewer", "auditor_viewer"],
  "/kyc-review":       ["super_admin", "fund_admin", "compliance_officer"],
  "/commitments":      ALL,
  "/capital-calls":    ["super_admin", "fund_admin", "fund_manager", "accountant", "auditor_viewer", "trustee_viewer"],
  "/payments":         ["super_admin", "fund_admin", "accountant", "auditor_viewer"],
  "/ledger":           ["super_admin", "fund_admin", "accountant", "auditor_viewer", "trustee_viewer"],
  "/chart-of-accounts":["super_admin", "fund_admin", "accountant", "auditor_viewer"],
  "/journal":          ["super_admin", "fund_admin", "accountant", "auditor_viewer"],
  "/nav":              ["super_admin", "fund_admin", "accountant", "fund_manager", "auditor_viewer", "trustee_viewer"],
  "/portfolio":        ["super_admin", "fund_admin", "fund_manager", "auditor_viewer", "trustee_viewer"],
  "/compliance":       ["super_admin", "fund_admin", "compliance_officer", "auditor_viewer", "trustee_viewer"],
  "/tasks":            ALL,
  "/documents":        ALL,
  "/reports":          ALL,
  "/report-templates": ["super_admin", "fund_admin", "compliance_officer", "auditor_viewer", "trustee_viewer"],
  "/distributions":    ["super_admin", "fund_admin", "fund_manager", "accountant", "auditor_viewer", "trustee_viewer"],
  "/derivatives":      ["super_admin", "fund_admin", "fund_manager", "accountant", "auditor_viewer", "trustee_viewer"],
  "/waterfall":        ["super_admin", "fund_admin", "fund_manager", "accountant", "auditor_viewer", "trustee_viewer"],
  "/investees":        ["super_admin", "fund_admin", "fund_manager", "compliance_officer", "auditor_viewer", "trustee_viewer"],
  "/deal-workflow":    ["super_admin", "fund_admin", "fund_manager", "compliance_officer", "auditor_viewer"],
  "/ops":              ["super_admin", "fund_admin", "fund_manager", "accountant", "compliance_officer"],
  "/analytics":        ["super_admin", "fund_admin", "fund_manager", "accountant", "compliance_officer", "auditor_viewer", "trustee_viewer"],
  "/fund-classes":     ["super_admin", "fund_admin", "fund_manager", "compliance_officer", "accountant", "auditor_viewer"],
  "/taxation":         ["super_admin", "fund_admin", "accountant", "compliance_officer", "auditor_viewer"],
  "/open-ended":       ["super_admin", "fund_admin", "fund_manager", "accountant", "relationship_manager", "auditor_viewer"],
  "/hwm":              ["super_admin", "fund_admin", "fund_manager", "accountant", "compliance_officer", "auditor_viewer"],
  "/investor/redeem":  ALL_PLUS_INVESTOR,
  "/branding":         ["super_admin", "fund_admin"],
  "/bulk-upload":      ["super_admin", "fund_admin", "accountant"],
  "/audit":            ["super_admin", "fund_admin", "compliance_officer", "auditor_viewer"],
  "/users":            ["super_admin", "fund_admin"],
  "/integrations":     ["super_admin", "fund_admin"],
  "/security":         ALL_PLUS_INVESTOR,
  "/help":             ALL_PLUS_INVESTOR,
  "/settings":         ["super_admin", "fund_admin"],
  "/settings/razorpay":["super_admin", "fund_admin"],
  "/settings/leegality":["super_admin", "fund_admin"],
  "/leads":            ["platform_admin"],
  "/distributors":     ["super_admin", "fund_admin"],
  "/distributor":      ["distributor"],
  "/profile":          [...ALL_PLUS_INVESTOR, "distributor"],
  "/billing":          [...ALL, "distributor"],
  "/admin/billing":    ["super_admin", "fund_admin"],
  "/ingestion":        ["super_admin", "fund_admin", "accountant", "rta_viewer"],
  "/admin/digest":     ["super_admin", "fund_admin"],
  "/admin/lp-nudge":   ["super_admin", "fund_admin"],
  "/admin/pricing":    ["super_admin", "fund_admin"],
  "/admin/demos":      ["platform_admin", "super_admin", "fund_admin"],
  "/platform/practices": ["platform_admin"],
  "/platform/impersonate": ["platform_admin"],
  "/owner/audit-log":  ["platform_admin"],
  "/owner/landing":    ["platform_admin"],
  "/owner/support":    ["platform_admin"],
  "/owner/whats-new":  ["platform_admin"],
  "/owner/trash":      ["platform_admin"],
  "/owner/bulk-cleanup": ["platform_admin"],
  "/owner/sitemap":      ["platform_admin"],
  "/owner/brand":        ["platform_admin"],
  "/proposal-templates": ["platform_admin"],
  "/investor/kyc":     ["investor"],
  "/compliance/history": ["super_admin", "fund_admin", "compliance_officer", "auditor_viewer", "trustee_viewer"],
};

// Mutation permissions — checked client-side to hide buttons.
// (Backend remains the source of truth via `require_roles`.)
export const CAN = {
  // Fund / client / scheme mutations
  manageMasters:       ["super_admin", "fund_admin"],
  // Investor mutations
  manageInvestors:     ["super_admin", "fund_admin", "fund_manager", "relationship_manager"],
  // KYC / compliance state
  approveKyc:          ["super_admin", "fund_admin", "compliance_officer"],
  updateCompliance:    ["super_admin", "fund_admin", "compliance_officer"],
  // Capital calls / distributions
  issueCapitalCall:    ["super_admin", "fund_admin", "fund_manager"],
  issueDistribution:   ["super_admin", "fund_admin", "fund_manager"],
  // Accounting
  postJournal:         ["super_admin", "fund_admin", "accountant"],
  approveJournal:      ["super_admin", "fund_admin"],
  postNav:             ["super_admin", "fund_admin", "accountant"],
  approveNav:          ["super_admin", "fund_admin"],
  // Documents
  uploadDocument:      ["super_admin", "fund_admin", "accountant", "compliance_officer", "fund_manager", "relationship_manager"],
  // Bulk upload
  bulkUpload:          ["super_admin", "fund_admin", "accountant"],
  // Admin
  manageUsers:         ["super_admin", "fund_admin"],
  manageIntegrations:  ["super_admin", "fund_admin"],
  // Chart of Accounts — accountants can add/rename, only fund_admin+ can archive.
  // Permanent delete is restricted to Owner (platform_admin) on the backend.
  manageChartOfAccounts:  ["super_admin", "fund_admin", "accountant"],
  archiveChartOfAccounts: ["super_admin", "fund_admin"],
  mergeChartOfAccounts:   ["super_admin", "fund_admin"],
  bulkImportChartOfAccounts: ["super_admin", "fund_admin", "accountant"],
  deleteChartOfAccounts:  [], // Owner-only via platform_admin bypass in isAllowed
};

export function isAllowed(role, ruleKey) {
  if (!role) return false;
  // Owner (platform_admin) transcends every practice, super_admin owns their
  // tenant — both bypass every per-permission allowlist below.
  if (role === "platform_admin") return true;
  if (role === "super_admin") return true;
  const allowed = CAN[ruleKey] || [];
  return allowed.includes(role);
}

export function canSeeRoute(role, path, extra = {}) {
  if (!role) return false;
  const roles = extra.roles || [];
  const isPlatformAdmin = extra.isPlatformAdmin || roles.includes("platform_admin");
  // Platform admin can see the platform console + everything super_admin can.
  if (path.startsWith("/platform/")) return isPlatformAdmin;
  if (path.startsWith("/owner/")) return isPlatformAdmin;
  if (role === "super_admin" || isPlatformAdmin) return true;
  const allowed = ROUTE_ROLES[path];
  if (!allowed) return true; // unknown route — default allow
  return allowed.includes(role);
}

export function isViewerOnly(role) {
  return VIEWER_ROLES.includes(role);
}
